import React from 'react';
import { Row, Col } from 'antd';
import { LeftCircleOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import COButton from 'shared/components/COButton';
import COSelect from 'shared/components/COSelect';
import COBadge from 'shared/components/COBadge';
import CreateProductStageNavigationBar from 'shared/components/CreateProductStageNavigationBar';
import CarbonEmissionScale from 'shared/components/CarbonEmissionScale';
import ProductOverviewHelper from 'shared/components/ProductOverviewHelper';
import ProductFlowMap from './ProductFlowMap';
import { ProductOverviewEnums } from 'enums/ProductOverviewEnums';
import { TabsKeys } from 'enums/ProductsViewEnums';
import { ProductMapRoots } from 'enums/ProductFlowMapEnums';
import { formatDateRange } from 'views/CompanySetup/companySetupConstants';
import styles from './ProductOverview.module.scss';
import { Product } from 'models/Product/product.model';
import { BadgeColors } from 'enums/COBadgeEnums';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as ApprovalReportIcon } from 'assets/icons/app-report.svg';
import { ReactComponent as ChatIcon } from 'assets/icons/chat.svg';
import reportingyearStyles from './reportingYear.module.scss';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';
import { CategoryOptions } from 'shared/utils/convertOptions';

interface ProductOverviewContentProps {
  newProduct: Product | undefined;
  productId: string | undefined;
  currentStage: number | undefined;
  stage1EmissionsTotal: number | undefined;
  stage2EmissionsTotal: number | undefined;
  stage3EmissionsTotal: number | undefined;
  stage4EmissionsTotal: number | undefined;
  stage1Emissions?: ProductRawMaterial[];
  stage2Emissions?: ProductRawMaterialTransportations[];
  stage3Emissions?: ProcessingFacilityEmissionData;
  stage4Emissions?: ProductDistributionEmissionData[];
  productSiblingsOptions: CategoryOptions[];
  isDisabled: boolean;
  inCreationPhase: boolean;
  badgeColor: BadgeColors;
  icon: React.ReactNode;
  handleGoBack: () => void;
  handleCloseButton: () => void;
  handleDownloadNavigation: () => void;
  handleReport: () => void;
  handleViewMoreNavigation: () => void;
  handleEditNavigation: () => void;
  handleOpenModal: () => void;
  setShowRejectReason: (value: boolean) => void;
  handleReportingYearChange: (value: string) => void;
  setProductOverviewLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProductOverviewContent: React.FC<ProductOverviewContentProps> = ({
  newProduct,
  productId,
  currentStage,
  stage1EmissionsTotal,
  stage2EmissionsTotal,
  stage3EmissionsTotal,
  stage4EmissionsTotal,
  stage1Emissions,
  stage2Emissions,
  stage3Emissions,
  stage4Emissions,
  productSiblingsOptions,
  isDisabled,
  inCreationPhase,
  badgeColor,
  icon,
  handleGoBack,
  handleCloseButton,
  handleDownloadNavigation,
  handleReport,
  handleViewMoreNavigation,
  handleEditNavigation,
  handleOpenModal,
  setShowRejectReason,
  handleReportingYearChange,
  setProductOverviewLoader,
}) => {
  return (
    <Row>
      <Col span={19}>
        <div className={styles.createProductTopRow}>
          <div className={styles.topLeftContainer}>
            <div className={styles.backButtonContainer} onClick={handleGoBack}>
              <LeftCircleOutlined />
            </div>
            <h2>{newProduct?.name}</h2>
            <div className={styles.badgeContainer}>
              <COBadge color={badgeColor}>
                {icon}
                <p className={styles.badgeFont}>{newProduct?.status}</p>
              </COBadge>
            </div>
          </div>

          <div className={styles.topRightContainer}>
            <COButton text onClick={handleCloseButton}>
              <p className={styles.closeBtn}>Close</p>
            </COButton>
            {newProduct?.status === TabsKeys.APPROVED && (
              <>
                <COButton text onClick={handleDownloadNavigation}>
                  <div className={styles.downloadBtn}>
                    <div className={styles.downloadIconContainer}>
                      <DownloadIcon
                        className={styles.productIcon}
                        onClick={handleDownloadNavigation}
                      />
                    </div>
                    {ProductOverviewEnums.DOWNLOAD}
                  </div>
                </COButton>

                <COButton text onClick={handleReport}>
                  <div className={styles.downloadBtn}>
                    <div className={styles.downloadIconContainer}>
                      <ApprovalReportIcon
                        className={styles.productIcon}
                        onClick={handleReport}
                      />
                    </div>
                    {ProductOverviewEnums.APPROVAL_REPORT}
                  </div>
                </COButton>
              </>
            )}
            {inCreationPhase === false && isDisabled ? (
              <COButton secondary onClick={handleViewMoreNavigation}>
                View more
              </COButton>
            ) : null}
            {!isDisabled && (
              <COButton secondary onClick={handleEditNavigation}>
                Edit
              </COButton>
            )}
            <div className={styles.rightContainer}>
              {newProduct?.status === TabsKeys.DRAFT ? (
                <COButton primary onClick={handleOpenModal}>
                  Submit for approval
                </COButton>
              ) : null}
              {newProduct?.status === TabsKeys.DECLINED && (
                <div
                  onClick={() => setShowRejectReason(true)}
                  className={styles.rejectedReason}>
                  <ChatIcon />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.reportingYearView}>
          {newProduct?.reportingPeriod &&
            formatDateRange(newProduct?.reportingPeriod)}
        </div>
        <div className={styles.secondRowContainer}>
          <div className={styles.totalContainer}>
            <h2>{ProductOverviewEnums.TOTAL}</h2>
          </div>
          {productSiblingsOptions?.length ? (
            <div className={styles.reportingYearDropdown}>
              <Formik
                initialValues={{
                  reportingYear: newProduct?.reportingPeriod
                    ? formatDateRange(newProduct?.reportingPeriod)
                    : '',
                }}
                enableReinitialize
                onSubmit={() => {}}>
                <div>
                  <COSelect
                    name="reportingYear"
                    options={productSiblingsOptions}
                    placeholderstring="Select year"
                    popupClassName={
                      reportingyearStyles.reportingYearDropdownField
                    }
                    onChange={handleReportingYearChange}
                    size="small"
                    allowClear={false}
                  />
                </div>
              </Formik>
            </div>
          ) : null}
        </div>

        <div className={styles.stageNavigationContainer}>
          <CreateProductStageNavigationBar
            productId={productId}
            stage={currentStage}
            stage1Total={Number(stage1EmissionsTotal?.toFixed(4))}
            stage2Total={Number(stage2EmissionsTotal?.toFixed(4))}
            stage3Total={Number(stage3EmissionsTotal?.toFixed(4))}
            stage4Total={Number(stage4EmissionsTotal?.toFixed(4))}
          />
        </div>
        <div className={styles.carbonEmissionScaleContainer}>
          <CarbonEmissionScale />
        </div>

        <div
          className={styles.mappingContainer}
          id={ProductMapRoots.PRODUCT_OVERVIEW_ROOT}>
          {stage1Emissions &&
            stage2Emissions &&
            stage3Emissions &&
            stage4Emissions && (
              <ProductFlowMap
                stage1Emissions={stage1Emissions}
                stage2Emissions={stage2Emissions}
                stage3Emissions={stage3Emissions}
                stage4Emissions={stage4Emissions}
                setProductOverviewLoader={setProductOverviewLoader}
                rootId={ProductMapRoots.PRODUCT_OVERVIEW_ROOT}
              />
            )}
        </div>
      </Col>
      <Col span={5}>
        <div className={styles.helperContainer}>
          <ProductOverviewHelper
            temperatureControl={newProduct?.temperatureControl}
            grossWeight={newProduct?.grossWeight}
            reportingPeriodUnits={newProduct?.reportingPeriodUnits}
            percentagePFGoods={newProduct?.processingFacilityCostPercentage}
            reportingPeriod={newProduct?.reportingPeriod}
            unit={newProduct?.netWeightUnit}
            productName={newProduct?.name}
            brandName={newProduct?.brandName}
            netWeight={newProduct?.netWeight}
            productImage={newProduct?.image}
            b2b={Number(newProduct?.b2b?.toFixed(4) ?? 0)}
            b2c={Number(newProduct?.b2c?.toFixed(4) ?? 0)}
          />
        </div>
      </Col>
    </Row>
  );
};

export default ProductOverviewContent;
